import { useState } from "react";
import { BookOpenCheck, Download, Loader2, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";

export default function RelocationGuide({ city = "", cityName = "" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const download = () => {
    const a = document.createElement("a");
    a.href = `${API}/relocation-guide.pdf${city ? `?city=${city}` : ""}`;
    a.download = `Moving-to-Southeast-Arizona${cityName ? `-${cityName.replace(/\s+/g, "-")}` : ""}-Relocation-Guide.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/relocation-guide", { email, name, city });
      setDone(true);
      toast.success("Your guide is on its way — the download should start now.");
      download();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please enter a valid email address.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="relocation-guide" className="relative overflow-hidden rounded-2xl border border-brand-gold/40 bg-white shadow-sm">
      <div className="grid md:grid-cols-12">
        <div className="md:col-span-7 p-7 sm:p-9">
          <p className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-bold">
            <MapPinned className="h-3.5 w-3.5" /> Free PDF download
          </p>
          <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-brand-navy-dark">Moving to {cityName || "Southeast Arizona"}?</h3>
          <p className="mt-3 max-w-lg text-sm sm:text-base leading-relaxed text-brand-slate">
            Get the free relocation guide — {cityName ? <>your <strong className="text-brand-navy-dark">{cityName} edition</strong> opens first, with</> : ""} all eleven Southeast Arizona communities inside. ZIPs, local highlights and the questions newcomers ask most.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-brand-slate">
            {[
              "11 community profiles with ZIPs and local highlights",
              city === "sonoita" ? "Bonus: the Sonoita wine route tasting loop" : "Drawn from 4,015 real almanac Q&As",
              "Instant download — a copy is emailed to you too",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-5 grain relative flex flex-col justify-center bg-brand-navy-dark p-7 sm:p-9">
          {done ? (
            <div data-testid="guide-success" className="text-center">
              <BookOpenCheck className="mx-auto h-9 w-9 text-brand-gold" />
              <p className="mt-4 font-display text-xl font-bold text-white">Enjoy the guide!</p>
              <p className="mt-2 text-sm text-white/65">A copy is also in your inbox — and Frank will see you stopped by.</p>
              <button
                data-testid="guide-download-again"
                onClick={download}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-red px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-red-dark"
              >
                <Download className="h-4 w-4" /> Download again
              </button>
            </div>
          ) : (
            <form data-testid="guide-form" onSubmit={submit} className="space-y-3">
              <input
                data-testid="guide-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name (optional)"
                className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
              />
              <input
                data-testid="guide-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com *"
                className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
              />
              <button
                data-testid="guide-submit-button"
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold px-6 py-3.5 text-sm font-bold text-brand-navy-dark transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {busy ? "Preparing…" : "Send Me the Free Guide"}
              </button>
              <p className="text-center text-[11px] text-white/45">No spam — Frank follows up personally, that's it.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
